import { useNavigate } from 'react-router-dom';
import Tour from 'reactour';

import { MatterList } from './components/matter-list';
import { OnboardingChecklist } from './components/onboarding-checklist';
import { WhereYouLeftOff } from './components/where-you-left-off';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { useCreateMatter } from '../../../api/mutations/useCreateMatter2.0';
import { Icon } from '../../components/icon';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetGlobalRecentSubsets } from '@/api/queries/useGetGlobalRecentSubsets';
import { useGetMatters } from '@/api/queries/useGetMatters';
import { useGetPersonalMatter } from '@/api/queries/useGetPersonalMatter';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { MattersWelcomeOnboarding } from '@/new/components/onboarding/matters-welcome-V2-onboarding';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { MATTERS_LIST_TOUR_STEPS } from '@/new/constants/tour-steps';
import { useTour } from '@/new/hooks/useTour';

export function Matters() {
  const { data: personalMatter } = useGetPersonalMatter();
  const { data: matters, isLoading: isLoadingMatters } = useGetMatters();
  const { data: recentSubsets } = useGetGlobalRecentSubsets();

  const nonDraftMatters = matters?.filter((matter) => !matter.isDraft);
  const hasMatters = (matters?.length ?? 0) > 0;

  const { mutateAsync: createMatter } = useCreateMatter();
  const { isOpen: isTourOpen, openTour, closeTour } = useTour('matters-list');
  const { hasPermission } = useHasPermission();
  const canCreateUpload = hasPermission(Permission.MatterCreateUpload);

  const navigate = useNavigate();

  const handleCreateMatter = async () => {
    await createMatter(undefined, {
      onSuccess: (data) => {
        trackCustomEvent(TrackingEventsV2.CreateMatter, { matterId: data.id });
        navigate(`/matters/new/${data.id}`);
      },
    });
  };

  return (
    <>
      <Breadcrumb items={[{ href: '/matters', label: 'Matters', active: true }]} />

      <PageContainer>
        <PageHeader>
          <PageTitle text="Matters" />
          {canCreateUpload && (
            <Button variant="primary" onClick={handleCreateMatter} data-tour="create-matter-btn">
              <Icon className="lucide--folder-plus h-4 w-4" />
              Create a New Matter
            </Button>
          )}
        </PageHeader>

        <div className="space-y-6" data-tour="matters-list">
          {recentSubsets && recentSubsets.length > 0 && <WhereYouLeftOff recentSubsets={recentSubsets} />}

          {!hasMatters && !isLoadingMatters && <OnboardingChecklist matters={nonDraftMatters} />}

          {!isLoadingMatters && hasMatters && <MatterList matters={matters} personalMatter={personalMatter} />}
        </div>
      </PageContainer>

      <MattersWelcomeOnboarding onTakeTour={openTour} />
      <Tour startAt={0} steps={MATTERS_LIST_TOUR_STEPS} isOpen={isTourOpen} onRequestClose={closeTour} />
    </>
  );
}
